import { useEffect, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import {
  ShoppingCart,
  Gift,
  Package,
  Heart,
  Star,
  Tag,
  Truck,
  ShoppingBag,
  CreditCard,
  Sparkles,
} from "lucide-react";
import logo from "@/assets/logo.webp";
import logoWhite from "@/assets/logo-white.webp";

// Διάφορα εικονίδια σκορπισμένα σε διάφορα σημεία της οθόνης
const FLOATING_ICONS = [
  { Icon: ShoppingCart, top: "14%", left: "12%", size: 40, delay: 0, dur: 3.4 },
  { Icon: Gift, top: "22%", left: "82%", size: 34, delay: 0.4, dur: 4.1 },
  { Icon: Package, top: "68%", left: "16%", size: 38, delay: 0.8, dur: 3.7 },
  { Icon: Heart, top: "76%", left: "78%", size: 30, delay: 0.2, dur: 4.4 },
  { Icon: Star, top: "40%", left: "8%", size: 26, delay: 1.1, dur: 3.2 },
  { Icon: Tag, top: "12%", left: "50%", size: 28, delay: 0.6, dur: 4.0 },
  { Icon: Truck, top: "84%", left: "44%", size: 36, delay: 1.4, dur: 3.9 },
  { Icon: ShoppingBag, top: "58%", left: "88%", size: 32, delay: 0.9, dur: 4.3 },
  { Icon: CreditCard, top: "50%", left: "70%", size: 28, delay: 1.6, dur: 3.5 },
  { Icon: Sparkles, top: "32%", left: "30%", size: 24, delay: 1.2, dur: 4.6 },
];

export function PageLoader() {
  const router = useRouter();
  const [show, setShow] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearSafety = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const hide = () => {
      clearSafety();
      setShow(false);
    };

    const unsubStart = router.subscribe("onBeforeNavigate", () => {
      setShow(true);
      clearSafety();
      // Δίχτυ ασφαλείας: ποτέ να μην κολλήσει η ροδέλα μόνιμα
      timeoutRef.current = setTimeout(() => setShow(false), 8000);
    });
    const unsubResolved = router.subscribe("onResolved", hide);

    return () => {
      clearSafety();
      unsubStart();
      unsubResolved();
    };
  }, [router]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 overflow-hidden bg-background/80 backdrop-blur-sm animate-fade-in">
      {/* Εικονίδια που κουνιούνται σε διάφορα σημεία */}
      {FLOATING_ICONS.map(({ Icon, top, left, size, delay, dur }, i) => (
        <Icon
          key={i}
          aria-hidden
          className="pointer-events-none absolute text-primary/40 animate-bob"
          style={{
            top,
            left,
            width: size,
            height: size,
            animationDuration: `${dur}s`,
            animationDelay: `${delay}s`,
          }}
        />
      ))}

      <img src={logo} alt="MrPanda" width={360} height={95} className="relative h-12 w-[182px] object-contain animate-pop-in dark:hidden" />
      <img src={logoWhite} alt="MrPanda" width={360} height={95} className="relative hidden h-12 w-[182px] object-contain animate-pop-in dark:block" />
      <div className="relative flex items-center gap-1.5" role="status" aria-label="Φόρτωση">
        <span className="h-2.5 w-2.5 rounded-full bg-primary animate-float-y" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary animate-float-y [animation-delay:0.15s]" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary animate-float-y [animation-delay:0.3s]" />
      </div>
      <p className="relative text-sm font-medium text-muted-foreground animate-pop-in">
        Φορτώνει…
      </p>
    </div>
  );
}